import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, LogOut, Loader2, Plus, Trash2 } from "lucide-react";
import {
  adminSaveContent,
  fetchContent,
  adminUploadImage,
  imageUrl,
  clearAdminPassword,
} from "@/lib/api";
import { ImageUpload } from "./ImageUpload";
import { AdminSubmissions } from "./AdminSubmissions";

const Field = ({ label, children }) => (
  <div className="space-y-2">
    <label className="block text-[10px] uppercase tracking-[0.3em] text-[#68919B]">
      {label}
    </label>
    {children}
  </div>
);

const TextInput = ({ value, onChange, placeholder, type = "text" }) => (
  <input
    type={type}
    value={value || ""}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    className="w-full bg-transparent border-b border-[#68919B]/40 py-2 text-[#3D5E68] focus:outline-none focus:border-[#3D5E68] transition-colors font-light"
  />
);

const TextArea = ({ value, onChange, rows = 3 }) => (
  <textarea
    value={value || ""}
    onChange={(e) => onChange(e.target.value)}
    rows={rows}
    className="w-full bg-transparent border border-[#68919B]/30 rounded p-3 text-[#3D5E68] focus:outline-none focus:border-[#3D5E68] transition-colors font-light resize-none"
  />
);

const Section = ({ title, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-[#68919B]/20 bg-[#F3F5E7]/60">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-[#EAEDDC]/50 transition-colors"
      >
        <h3 className="font-serif text-xl text-[#3D5E68]">{title}</h3>
        <span className="text-[#68919B] text-sm">{open ? "−" : "+"}</span>
      </button>
      {open && <div className="px-6 pb-8 pt-2 space-y-6 border-t border-[#68919B]/15">{children}</div>}
    </div>
  );
};

export const AdminDashboard = ({ onLogout }) => {
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("content"); // "content" | "submissions"

  useEffect(() => {
    fetchContent()
      .then((res) => setContent(res.data.data || {}))
      .catch(() => toast.error("Kunne ikke laste innhold"))
      .finally(() => setLoading(false));
  }, []);

  const update = (path, value) => {
    setContent((prev) => {
      const next = structuredClone(prev);
      const keys = path.split(".");
      let cur = next;
      for (let i = 0; i < keys.length - 1; i++) {
        cur[keys[i]] = cur[keys[i]] || {};
        cur = cur[keys[i]];
      }
      cur[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await adminSaveContent(content);
      toast.success("Lagret!");
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Sesjonen er utløpt — logg inn på nytt");
        onLogout();
      } else {
        toast.error("Lagring feilet");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    clearAdminPassword();
    onLogout();
  };

  if (loading || !content) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F3F5E7]">
        <Loader2 className="animate-spin text-[#68919B]" size={28} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#EAEDDC]/40 pb-20">
      <header className="sticky top-0 z-30 bg-[#F3F5E7]/95 backdrop-blur-md border-b border-[#68919B]/20">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between flex-wrap gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.35em] text-[#68919B]">
              Admin
            </p>
            <h1 className="font-serif text-2xl text-[#3D5E68]">
              {view === "content" ? "Innholdsstyring" : "Svar fra gjester"}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            {view === "content" && (
              <button
                onClick={save}
                disabled={saving}
                data-testid="admin-save-button"
                className="flex items-center gap-2 px-6 py-3 bg-[#68919B] text-[#F3F5E7] text-xs uppercase tracking-[0.25em] hover:bg-[#3D5E68] transition-colors disabled:opacity-50"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                Lagre
              </button>
            )}
            <button
              onClick={handleLogout}
              data-testid="admin-logout-button"
              className="flex items-center gap-2 px-4 py-3 border border-[#68919B] text-[#68919B] text-xs uppercase tracking-[0.25em] hover:bg-[#68919B] hover:text-[#F3F5E7] transition-colors"
            >
              <LogOut size={14} /> Logg ut
            </button>
          </div>
        </div>
        {/* Tab switcher */}
        <div className="max-w-5xl mx-auto px-6 flex gap-2 border-t border-[#68919B]/10">
          {[
            { key: "content", label: "Innhold" },
            { key: "submissions", label: "Svar" },
          ].map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setView(t.key)}
              data-testid={`admin-tab-${t.key}`}
              className={`px-5 py-3 text-xs uppercase tracking-[0.25em] transition-colors border-b-2 ${
                view === t.key
                  ? "border-[#3D5E68] text-[#3D5E68]"
                  : "border-transparent text-[#5A7682] hover:text-[#3D5E68]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </header>

      {view === "submissions" ? (
        <main className="max-w-6xl mx-auto px-6 py-10">
          <AdminSubmissions />
        </main>
      ) : (
        <main className="max-w-5xl mx-auto px-6 py-10 space-y-5">
        {/* HERO */}
        <Section title="Hero (forsiden)" defaultOpen>
          <Field label="Tekst over navn (eyebrow)">
            <TextInput
              value={content.hero?.eyebrow}
              onChange={(v) => update("hero.eyebrow", v)}
            />
          </Field>
          <Field label="Tekst under navn (undertittel)">
            <TextInput
              value={content.hero?.subtitle}
              onChange={(v) => update("hero.subtitle", v)}
            />
          </Field>
          <ImageUpload
            label="Bakgrunnsbilde"
            value={content.hero?.image_id}
            onChange={(id) => update("hero.image_id", id)}
            aspect="aspect-[16/9]"
          />
        </Section>

        {/* STORY */}
        <Section title="Vår historie (Kapittel I)">
          <Field label="Sitat (kursivert, øverst)">
            <TextArea
              rows={2}
              value={content.story?.quote}
              onChange={(v) => update("story.quote", v)}
            />
          </Field>
          {(content.story?.paragraphs || []).map((p, i) => (
            <Field key={i} label={`Avsnitt ${i + 1}`}>
              <TextArea
                value={p}
                rows={4}
                onChange={(v) => {
                  const arr = [...content.story.paragraphs];
                  arr[i] = v;
                  update("story.paragraphs", arr);
                }}
              />
            </Field>
          ))}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() =>
                update("story.paragraphs", [...(content.story?.paragraphs || []), ""])
              }
              className="text-xs uppercase tracking-[0.25em] text-[#68919B] flex items-center gap-2 hover:text-[#3D5E68]"
            >
              <Plus size={14} /> Legg til avsnitt
            </button>
            {(content.story?.paragraphs || []).length > 1 && (
              <button
                type="button"
                onClick={() =>
                  update(
                    "story.paragraphs",
                    content.story.paragraphs.slice(0, -1),
                  )
                }
                className="text-xs uppercase tracking-[0.25em] text-[#9E0232] flex items-center gap-2 hover:opacity-80"
              >
                <Trash2 size={14} /> Fjern siste
              </button>
            )}
          </div>
          <Field label="Bildetekst">
            <TextInput
              value={content.story?.caption}
              onChange={(v) => update("story.caption", v)}
            />
          </Field>
          <ImageUpload
            label="Historie-bilde"
            value={content.story?.image_id}
            onChange={(id) => update("story.image_id", id)}
            aspect="aspect-[4/5]"
          />
        </Section>

        {/* PROGRAM */}
        <Section title="Programmet (Kapittel II)">
          <Field label="Undertittel">
            <TextInput
              value={content.program?.subtitle}
              onChange={(v) => update("program.subtitle", v)}
            />
          </Field>
          {(content.program?.days || []).map((day, di) => (
            <div key={di} className="border border-[#68919B]/20 p-5 space-y-4 bg-[#F3F5E7]">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Dag (Fredag / Lørdag …)">
                  <TextInput
                    value={day.label}
                    onChange={(v) => {
                      const arr = [...content.program.days];
                      arr[di] = { ...arr[di], label: v };
                      update("program.days", arr);
                    }}
                  />
                </Field>
                <Field label="Dato">
                  <TextInput
                    value={day.date}
                    onChange={(v) => {
                      const arr = [...content.program.days];
                      arr[di] = { ...arr[di], date: v };
                      update("program.days", arr);
                    }}
                  />
                </Field>
              </div>
              <Field label="Undertittel for dagen">
                <TextInput
                  value={day.subtitle}
                  onChange={(v) => {
                    const arr = [...content.program.days];
                    arr[di] = { ...arr[di], subtitle: v };
                    update("program.days", arr);
                  }}
                />
              </Field>
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#68919B] pt-2">
                Hendelser
              </p>
              {(day.events || []).map((ev, ei) => (
                <div key={ei} className="grid grid-cols-12 gap-3 items-start bg-[#EAEDDC]/40 p-3">
                  <div className="col-span-2">
                    <TextInput
                      value={ev.time}
                      onChange={(v) => {
                        const arr = [...content.program.days];
                        const evs = [...arr[di].events];
                        evs[ei] = { ...evs[ei], time: v };
                        arr[di] = { ...arr[di], events: evs };
                        update("program.days", arr);
                      }}
                      placeholder="14:00"
                    />
                  </div>
                  <div className="col-span-4">
                    <TextInput
                      value={ev.title}
                      onChange={(v) => {
                        const arr = [...content.program.days];
                        const evs = [...arr[di].events];
                        evs[ei] = { ...evs[ei], title: v };
                        arr[di] = { ...arr[di], events: evs };
                        update("program.days", arr);
                      }}
                      placeholder="Tittel"
                    />
                  </div>
                  <div className="col-span-5">
                    <TextInput
                      value={ev.desc}
                      onChange={(v) => {
                        const arr = [...content.program.days];
                        const evs = [...arr[di].events];
                        evs[ei] = { ...evs[ei], desc: v };
                        arr[di] = { ...arr[di], events: evs };
                        update("program.days", arr);
                      }}
                      placeholder="Beskrivelse"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const arr = [...content.program.days];
                      const evs = arr[di].events.filter((_, idx) => idx !== ei);
                      arr[di] = { ...arr[di], events: evs };
                      update("program.days", arr);
                    }}
                    className="col-span-1 text-[#9E0232] hover:opacity-70 pt-2"
                    aria-label="Fjern hendelse"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => {
                  const arr = [...content.program.days];
                  arr[di] = {
                    ...arr[di],
                    events: [...(arr[di].events || []), { time: "", title: "", desc: "" }],
                  };
                  update("program.days", arr);
                }}
                className="text-xs uppercase tracking-[0.25em] text-[#68919B] flex items-center gap-2"
              >
                <Plus size={14} /> Legg til hendelse
              </button>
            </div>
          ))}
        </Section>

        {/* LOCATION */}
        <Section title="Sted (Kapittel III)">
          <Field label="Undertittel">
            <TextInput
              value={content.location?.subtitle}
              onChange={(v) => update("location.subtitle", v)}
            />
          </Field>
          <Field label="Tid (over hovedteksten)">
            <TextInput
              value={content.location?.time_label}
              onChange={(v) => update("location.time_label", v)}
            />
          </Field>
          <Field label="Hovedtekst">
            <TextArea
              value={content.location?.heading}
              onChange={(v) => update("location.heading", v)}
            />
          </Field>
          <Field label="Adresse">
            <TextInput
              value={content.location?.address}
              onChange={(v) => update("location.address", v)}
            />
          </Field>
          <Field label="Med bil">
            <TextArea
              value={content.location?.by_car}
              onChange={(v) => update("location.by_car", v)}
            />
          </Field>
          <Field label="Med båt">
            <TextArea
              value={content.location?.by_boat}
              onChange={(v) => update("location.by_boat", v)}
            />
          </Field>
          <Field label="Med buss">
            <TextArea
              value={content.location?.by_bus}
              onChange={(v) => update("location.by_bus", v)}
            />
          </Field>
          <Field label="Google Maps-lenke">
            <TextInput
              value={content.location?.maps_url}
              onChange={(v) => update("location.maps_url", v)}
            />
          </Field>
          <ImageUpload
            label="Sted-bilde"
            value={content.location?.image_id}
            onChange={(id) => update("location.image_id", id)}
            aspect="aspect-[3/4]"
          />
        </Section>

        {/* GALLERY */}
        <Section title="Galleri (Kapittel IV)">
          <p className="text-sm text-[#5A7682] font-light">
            Dra og slipp bilder for å legge til galleri-bilder. Du kan ha så mange du vil.
          </p>
          <GallerySection
            ids={content.gallery?.image_ids || []}
            onChange={(ids) => update("gallery.image_ids", ids)}
          />
        </Section>

        {/* GAVELISTE */}
        <Section title="Gaveliste (Kapittel V)">
          <Field label="Introtekst">
            <TextArea
              value={content.gaveliste?.intro}
              onChange={(v) => update("gaveliste.intro", v)}
            />
          </Field>

          <p className="text-[10px] uppercase tracking-[0.3em] text-[#68919B] pt-4">
            Gavelistepunkter
          </p>
          {(content.gaveliste?.custom_items || []).map((item, i) => (
            <div key={i} className="border border-[#68919B]/20 p-5 space-y-4 bg-[#F3F5E7]">
              <div className="flex items-center justify-between">
                <p className="font-serif text-lg text-[#3D5E68]">Punkt {i + 1}</p>
                <button
                  type="button"
                  onClick={() => {
                    const arr = (content.gaveliste?.custom_items || []).filter((_, idx) => idx !== i);
                    update("gaveliste.custom_items", arr);
                  }}
                  className="text-[10px] uppercase tracking-[0.25em] text-[#9E0232] hover:opacity-70 flex items-center gap-1.5"
                >
                  <Trash2 size={12} /> Fjern punkt
                </button>
              </div>
              <Field label="Tittel">
                <TextInput
                  value={item.title}
                  onChange={(v) => {
                    const arr = [...(content.gaveliste?.custom_items || [])];
                    arr[i] = { ...arr[i], title: v };
                    update("gaveliste.custom_items", arr);
                  }}
                  placeholder="F.eks. Bryllupsreise"
                />
              </Field>
              <Field label="Beskrivelse">
                <TextArea
                  value={item.desc}
                  onChange={(v) => {
                    const arr = [...(content.gaveliste?.custom_items || [])];
                    arr[i] = { ...arr[i], desc: v };
                    update("gaveliste.custom_items", arr);
                  }}
                />
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Lenke (URL)">
                  <TextInput
                    value={item.link_url}
                    onChange={(v) => {
                      const arr = [...(content.gaveliste?.custom_items || [])];
                      arr[i] = { ...arr[i], link_url: v };
                      update("gaveliste.custom_items", arr);
                    }}
                    placeholder="https://..."
                  />
                </Field>
                <Field label="Knapp-tekst">
                  <TextInput
                    value={item.link_label}
                    onChange={(v) => {
                      const arr = [...(content.gaveliste?.custom_items || [])];
                      arr[i] = { ...arr[i], link_label: v };
                      update("gaveliste.custom_items", arr);
                    }}
                    placeholder="F.eks. Åpne gaveliste"
                  />
                </Field>
              </div>
              <ImageUpload
                label="Bilde (valgfritt)"
                value={item.image_id}
                onChange={(id) => {
                  const arr = [...(content.gaveliste?.custom_items || [])];
                  arr[i] = { ...arr[i], image_id: id };
                  update("gaveliste.custom_items", arr);
                }}
                aspect="aspect-[4/3]"
              />
            </div>
          ))}
          <button
            type="button"
            onClick={() => {
              const arr = [
                ...(content.gaveliste?.custom_items || []),
                { title: "", desc: "", image_id: null, link_url: "", link_label: "" },
              ];
              update("gaveliste.custom_items", arr);
            }}
            className="text-xs uppercase tracking-[0.25em] text-[#68919B] flex items-center gap-2 hover:text-[#3D5E68]"
          >
            <Plus size={14} /> Legg til punkt
          </button>
        </Section>

        {/* ACCOMMODATION */}
        <Section title="Overnatting (Kapittel VII)">
          <Field label="Undertittel">
            <TextArea
              value={content.accommodation?.subtitle}
              onChange={(v) => update("accommodation.subtitle", v)}
            />
          </Field>
        </Section>

        {/* RSVP */}
        <Section title="RSVP (Kapittel VI)">
          <Field label="Svarfrist (vises i teksten)">
            <TextInput
              value={content.rsvp?.deadline}
              onChange={(v) => update("rsvp.deadline", v)}
            />
          </Field>
        </Section>

        {/* FOOTER */}
        <Section title="Footer">
          <Field label="Sluttmelding">
            <TextInput
              value={content.footer?.message}
              onChange={(v) => update("footer.message", v)}
            />
          </Field>
        </Section>

        <div className="pt-6 flex justify-end">
          <button
            onClick={save}
            disabled={saving}
            data-testid="admin-save-button-bottom"
            className="flex items-center gap-2 px-10 py-4 bg-[#68919B] text-[#F3F5E7] text-xs uppercase tracking-[0.25em] hover:bg-[#3D5E68] transition-colors disabled:opacity-50"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Lagre endringer
          </button>
        </div>
      </main>
      )}
    </div>
  );
};

const GallerySection = ({ ids, onChange }) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [drag, setDrag] = useState(false);

  const uploadMany = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    const newIds = [...ids];
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      if (!f.type.startsWith("image/")) continue;
      try {
        const res = await adminUploadImage(f, setProgress);
        newIds.push(res.data.id);
      } catch (err) {
        toast.error(`${f.name} feilet`);
      }
    }
    onChange(newIds);
    setUploading(false);
    setProgress(0);
    toast.success("Bilder lastet opp");
  };

  return (
    <div className="space-y-4">
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDrag(false);
          uploadMany(Array.from(e.dataTransfer.files || []));
        }}
        className={`border-2 border-dashed py-10 text-center cursor-pointer transition-colors bg-[#F3F5E7] ${
          drag ? "border-[#68919B] bg-[#EAEDDC]" : "border-[#68919B]/40 hover:border-[#68919B]"
        }`}
        onClick={() => document.getElementById("gallery-input")?.click()}
      >
        {uploading ? (
          <div>
            <Loader2 size={26} className="mx-auto text-[#68919B] animate-spin" />
            <p className="mt-2 text-sm text-[#3D5E68]">Laster opp… {progress}%</p>
          </div>
        ) : (
          <div>
            <Plus size={26} className="mx-auto text-[#68919B]" />
            <p className="mt-2 text-sm text-[#3D5E68]">Dra og slipp bilder hit</p>
          </div>
        )}
        <input
          id="gallery-input"
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => uploadMany(Array.from(e.target.files || []))}
        />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {ids.map((id) => (
          <div key={id} className="relative group aspect-square">
            <img
              src={imageUrl(id)}
              alt=""
              className="w-full h-full object-cover"
            />
            <button
              type="button"
              onClick={() => onChange(ids.filter((x) => x !== id))}
              className="absolute top-2 right-2 bg-[#F3F5E7]/90 text-[#9E0232] p-2 opacity-0 group-hover:opacity-100 transition-opacity"
              aria-label="Fjern bilde"
            >
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};
